import Label from '@components/forms/Label'
import Button from '@components/shared/Button'
import InlineNotification from '@components/shared/InlineNotification'
import { useWallet } from '@solana/wallet-adapter-react'
import { useCallback, useState } from 'react'
import {
  isUsersApiEnabled,
  USER_TOKEN_KEY,
  USERS_API_URL,
} from 'utils/constants'
import { notify } from 'utils/notifications'
import { useIsAuthorized } from 'hooks/useIsAuthorized'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { login } from 'utils/login'
import store from 'stores/store'
import { useProfileDetails } from 'components/profile/useProfileDetails'
import ButtonGroup from '@components/forms/ButtonGroup'
import { useTranslation } from 'next-i18next'
import { ModalProps } from 'types/modal'
import Modal from '@components/shared/Modal'
import { sleep } from 'utils'

const ActivatePartnerModal = ({ isOpen, onClose }: ModalProps) => {
  const { t } = useTranslation(['profile', 'messages'])
  const { authenticated, loadingUser } = useIsAuthorized()
  const { profileDetails, refetchProfileDetails } = useProfileDetails()
  const [token, setToken] = useLocalStorage(USER_TOKEN_KEY)
  const { publicKey, signMessage, signTransaction } = useWallet()
  const [loadUpdateProfile, setLoadUpdateProfile] = useState(false)
  const [updateError, setUpdateError] = useState('')
  const [isUsingLedger, setIsUsingLedger] = useState<'Yes' | 'No'>('No')

  const handleAuthError = useCallback(
    (error: unknown) => {
      setUpdateError(t('form.error'))
      setLoadUpdateProfile(false)
      console.error('Authentication error:', error)
    },
    [t],
  )

  const startPartnerAccount = useCallback(async () => {
    if (
      !profileDetails ||
      loadUpdateProfile ||
      !publicKey ||
      !signMessage ||
      !signTransaction
    ) {
      return
    }
    let authToken = token
    setLoadUpdateProfile(true)
    // handle authentication if needed
    if (!authenticated) {
      const connection = store.getState().connection
      const loginType = isUsingLedger === 'Yes' ? 'ledger' : 'siws'
      try {
        const authResponse = await login(
          loginType,
          { publicKey, signMessage, signTransaction },
          connection,
          { errorTitle: t('messages:errors.error') },
        )
        if (!authResponse?.token) {
          setLoadUpdateProfile(false)
          return
        }
        authToken = authResponse.token
        setToken(authToken)
      } catch (error) {
        handleAuthError(error)
        return
      }
    }
    if (!authToken) {
      setLoadUpdateProfile(false)
      return
    }
    if (!isUsersApiEnabled()) {
      setUpdateError(t('form.error'))
      setLoadUpdateProfile(false)
      return
    }
    try {
      const response = await fetch(`${USERS_API_URL}/user/partner-start`, {
        method: 'POST',
        headers: {
          authorization: authToken,
        },
      })

      if (!response.ok) {
        setUpdateError(t('form.error'))
        console.error('Start referral account error:', {
          status: response.status,
          response: await response.json(),
        })
        return
      }

      await refetchProfileDetails()
      await sleep(1000)
      onClose()
      notify({
        type: 'success',
        title: t('form.success'),
      })
    } catch (e) {
      setUpdateError(t('form.error'))
      console.error('Start referral account error:', e)
    } finally {
      setLoadUpdateProfile(false)
    }
  }, [
    authenticated,
    handleAuthError,
    isUsingLedger,
    loadUpdateProfile,
    onClose,
    profileDetails,
    publicKey,
    refetchProfileDetails,
    setToken,
    signMessage,
    signTransaction,
    t,
    token,
  ])

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {profileDetails ? (
        <>
          {updateError ? (
            <div className="py-3">
              <InlineNotification type="error" desc={updateError} />
            </div>
          ) : null}
          <h2 className="mb-2 text-center md:text-lg">
            {t('activate-partner-modal.activate-title')}
          </h2>
          <p className="mb-4 text-center text-th-fgd-2">
            {t('activate-partner-modal.activate-description')}
          </p>
          {!authenticated && !loadingUser ? (
            <div>
              <Label text={t('form.ledger')} />
              <ButtonGroup
                activeValue={isUsingLedger}
                values={['Yes', 'No']}
                onChange={setIsUsingLedger}
                names={[t('form.ledger-yes'), t('form.ledger-no')]}
              />
              <div className="mt-1.5">
                <InlineNotification
                  type="info"
                  desc={t('form.ledger-info')}
                  hideBorder
                  hidePadding
                />
              </div>
            </div>
          ) : null}
          <div className="mt-6">
            <Button
              className={'w-full'}
              onClick={startPartnerAccount}
              disabled={loadingUser || loadUpdateProfile}
            >
              {t('activate-partner-modal.activate')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <h2 className="mb-2 text-center md:text-lg">
            {t('activate-partner-modal.no-profile-title')}
          </h2>
          <p className="mb-4 text-center text-th-fgd-2">
            {t('activate-partner-modal.no-profile-description')}
          </p>
          <Button className={'w-full'} onClick={onClose}>
            {t('activate-partner-modal.close')}
          </Button>
        </>
      )}
    </Modal>
  )
}

export default ActivatePartnerModal
